<template>
  <div
    v-if="product"
    class="member-item medium-padding-bottom-z small-margin-bottom-xs large-margin-bottom-m grid-x"
    :data-product-id="props.productId"
    :data-test-product-id="props.productId"
  >
    <ProductTitle v-if="isMobile" :product-url="productUrl" class="margin-bottom-m" allow-opening-in-new-tab />
    <MarketPlaceTitle v-if="isMcomMarketplace && isMobile" />
    <div :id="containerId" class="image-container cell medium-5 large-3 large-margin-bottom-m">
      <a :href="productUrl" class="product-link" target="_blank" aria-label="product">
        <Picture
          :image-url="primaryImage"
          :alt="`${productName} image`"
          v-bind="primaryImage ? { 'image-intrinsic-width': imageIntrinsicWidth } : {}"
          :ai-image
        />
      </a>
      <template v-if="isAddToListVisible && isProductAvailable">
        <AddToList :container-id="containerId" />
      </template>
    </div>
    <div class="order-panel cell medium-6 medium-offset-1 large-8">
      <div class="product-info">
        <ProductTitle v-if="!isMobile" tag="h3" :product-url="productUrl" allow-opening-in-new-tab />
        <MarketPlaceTitle v-if="isMcomMarketplace && !isMobile" />
        <ProductTraits v-if="isProductAvailable" :member-product-url="productUrl" allow-opening-in-new-tab>
          <template #shop-more>
            <ShopMoreFacade v-if="isMcom && isShopMoreStandardEnabled" />
          </template>
        </ProductTraits>
        <OnDisplay v-if="isBigTicketItemFlag && showOnDisplay" :product-id="props.productId" condensed />
      </div>
      <div v-if="isProductAvailable" class="add-to-cart">
        <ProtectionPlanBigTicket v-if="isProtectionPlanEligible && !killswitches.isWNMBigTicketEnabled" />
        <div v-if="isQuantityEnabled" class="margin-bottom-m">
          <QuantitySelector v-model="quantity" :min="minQuantity" :max="maxQuantity" size="small" />
        </div>

        <template v-if="isMcom">
          <TransitionExpand
            :should-render-content="isAvailabilityEnabled"
            :is-animated="killswitches.isDnFExpandAnimated"
            class="margin-bottom-m"
            data-testid="availability"
          >
            <Availability :is-chip-fulfillment-enabled="false" />
          </TransitionExpand>

          <AvailabilityBigTicket v-if="isBigTicketItemFlag" class="margin-bottom-s" />
        </template>

        <BTZipCodeSlideout v-if="isBigTicketItemFlag" />

        <CTAButtons v-if="callToActionButtons" :class="margin" :buttons-inline="false" :sticky-footer-enabled="false" />
        <ProductEngagementMcom v-if="productHasEngagementPill" class="show-for-medium" />

        <template v-if="isBcom">
          <TransitionExpand
            :should-render-content="isAvailabilityEnabled"
            :is-animated="killswitches.isDnFExpandAnimated"
            class="margin-bottom-m"
            data-testid="availability"
          >
            <Availability :is-chip-fulfillment-enabled="false" />
          </TransitionExpand>

          <AvailabilityBigTicket v-if="isBigTicketItemFlag" class="margin-bottom-s" />
        </template>
      </div>
      <UnavailableProductDetails v-else class="unavailable-details" />
    </div>
  </div>
</template>

<script setup>
import { useCommonProductGetters } from '~/composables/useCommonProductGetters';
import { computed, defineAsyncComponent } from 'vue';
import Picture from '@atomic-ui/picture/Picture';
import QuantitySelector from '@atomic-ui/quantitySelector/QuantitySelector';
import { useCreateTypeStore } from '@nuxt-common/media-card-large/app/modules/type';
import TypeParser from '@nuxt-common/media-card-large/app/modules/type/parsers/TypeParser';
import useProductStore from '~/stores/useProductStore';
import Availability from '~/modules/availability/components/AvailabilityView';
import AvailabilityBigTicket from '~/modules/availability/components/AvailabilityBigTicket';
import BTZipCodeSlideout from '~/modules/availability/components/slideouts/BTZipCodeSlideout';
import { useAvailability } from '~/modules/availability/composables/useAvailability';
import { useBigTicketATBValidatorProvide } from '~/modules/availability/composables/useBigTicketATBValidator';
import { useAvailabilityBigTicketProvide } from '~/modules/availability/composables/useAvailabilityBigTicket';
import { useBigTicketDeliveryMethodsProvide } from '~/modules/availability/composables/useBigTicketDeliveryMethods';
import { useProductProvide } from '~/modules/product/composables/useProductProvide';
import useBopsUpcOverride from '~/modules/availability/composables/useBopsUpcOverride';
import { useQuantity, useQuantityProvide } from '~/modules/product/composables/useQuantity';
import useFeatureEligibility from '~/modules/featureControl/composables/useFeatureEligibility';
import { useProductFlags } from '~/modules/featureControl/composables/useProductFlags';
import useKillswitches from '~/modules/featureControl/composables/useKillswitches';

import ProtectionPlanBigTicket from '~/modules/protection-plan/components/ProtectionPlanBigTicket';
import AddToList from '~/modules/add-to-list/components/AddToList';
import CTAButtons from '~/components/ctaButtons/CTAButtons';
import ProductTitle from '~/components/productTitle/ProductTitle.common';
import ProductTraits from '~/components/ProductCard/ProductTraits';
import MarketPlaceTitle from '~/components/marketPlace/MarketPlaceTitle';
import OnDisplay from '~/components/common/OnDisplay';
import UnavailableProductDetails from '~/components/unavailable/UnavailableProductDetails.vue';
import { useUpcValidatorProvide } from '~/modules/upc/composables/useUpcValidator';
import TransitionExpand from '~/components/common/TransitionExpand';

import { useImagesProvide } from '~/composables/images/useImages';
import { useBrand } from '~/composables/useBrand';
import { useDeviceType } from '~/composables/utils/useDeviceType';
import { useMarketPlace } from '~/composables/useMarketPlace';
import useExperiments from '~/stores/useExperimentsStore';
import { useProductPriceProvide } from '~/modules/price/composables/useProductPriceProvide';
import ShopMoreFacade from '~/modules/shop-more/components/ShopMoreFacade';
import { AI_WATERMARK_CONFIG } from '~/constants/common';

const { killswitches } = useKillswitches();

const props = defineProps({
  productId: {
    type: [Number, String],
    required: true,
  },
});

const {
  experimentFlags: { isShopMoreStandardEnabled, isDefaultUpcSelectionEnabled },
} = useExperiments();

function getProduct() {
  const memberProducts = useProductStore().getAllMembers;
  const product = memberProducts[props.productId];
  useProductProvide(props.productId, product);
  useBopsUpcOverride();
  return product;
}

const product = getProduct();

useQuantityProvide();
useUpcValidatorProvide();
useMarketPlace();
useProductPriceProvide({ id: props.productId, product });
useAvailabilityBigTicketProvide();
useBigTicketDeliveryMethodsProvide();
useBigTicketATBValidatorProvide();

const ProductEngagementMcom = defineAsyncComponent(() => import('~/components/ProductEngagement.mcom'));

const { callToActionButtons, isQuantityEnabled, marketPlaceItemEnabled, productHasEngagementPill, isAddToListVisible } = useFeatureEligibility();
const { isBigTicketItemFlag, isAiGeneratedMetaData } = useProductFlags();
const { isAvailabilityEnabled } = useAvailability();
const { isMcom, isBcom } = useBrand();
const { isMobile } = useDeviceType();

// TODO handle in ssr for member products
// this is needed since ssr only parses for page product
useCreateTypeStore({
  id: props.productId,
  data: TypeParser(product, { defaultToFirst: isMcom || isDefaultUpcSelectionEnabled }),
});

const { primaryImageData } = useImagesProvide();

const { productUrl, productName, isProductAvailable } = useCommonProductGetters();

const primaryImage = computed(() => primaryImageData.value.path);

const showOnDisplay = computed(
  () => (!isMcom && killswitches.isBcomBigTicketSeeItInStoreEnabled) || (isMcom && killswitches.isMcomBigTicketSeeItInStoreEnabled),
);

const containerId = computed(() => `image-container-${props.productId}`);

const isProtectionPlanEligible = computed(() => product?.detail?.flags.protectionPlanEligible);

const { quantity, minQuantity, maxQuantity } = useQuantity();

const margin = productHasEngagementPill ? '' : 'margin-bottom-m';

const isMcomMarketplace = marketPlaceItemEnabled && isMcom && isProductAvailable;

const imageIntrinsicWidth = 328;
const aiImage = isAiGeneratedMetaData ? AI_WATERMARK_CONFIG.memberOfMaster : undefined;
</script>

<style lang="scss" scoped>
.order-panel {
  display: flex;
  flex-flow: column nowrap;
}
.image-container {
  position: relative;
}
.wishlist {
  position: absolute;
  top: 0;
  right: 14px;
  cursor: pointer;
}
.product-link {
  text-decoration: none;
}

@include breakpoint(large up) {
  .order-panel {
    flex-flow: row nowrap;
  }
  .member-item {
    flex-flow: row wrap;
  }
  .product-info {
    width: 50%;
  }
  .add-to-cart,
  .unavailable-details {
    width: 50%;
    padding-left: 12.5%;
  }
}
</style>
